import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  ClosedPositionData,
  PositionData,
} from "@crypto-trading/common/browser";
import { Loader2, Share2 } from "lucide-react";
import QRCode from "qrcode";
import { shareURL } from "@telegram-apps/sdk-react";

interface PnlCardShareProps {
  position: PositionData | ClosedPositionData;
  userDisplayName: string;
  referralLink?: string | null;
}

type CardTemplate = "profit" | "loss" | "neutral";

const CARD_TEMPLATE_PATHS: Record<CardTemplate, string> = {
  profit: "/pnl-card/cardTemplateProfit.png",
  loss: "/pnl-card/cardTemplateLoss.png",
  neutral: "/pnl-card/cardTemplate.png",
};

const CARD_WIDTH = 1440;
const CARD_HEIGHT = 960;
const QR_CODE_SIZE = 216;
const QR_CODE_POSITION = { x: 92, y: 645 };
const DEFAULT_BOT_LINK = "https://t.me/triggerdotfun_bot";

const MESSAGE_AREA = {
  x: 925,
  y: 362,
  width: 371,
  height: 453,
};

const isClosedPosition = (
  position: PositionData | ClosedPositionData
): position is ClosedPositionData => {
  return "realizedPnl" in position;
};

const isOpenPosition = (
  position: PositionData | ClosedPositionData
): position is PositionData => {
  return !isClosedPosition(position);
};

const getOpenPosition = (
  position: PositionData | ClosedPositionData
): PositionData | null => {
  return isOpenPosition(position) ? position : null;
};

const formatUsd = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "N/A";
  }
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: value >= 100 ? 0 : 2,
    maximumFractionDigits: value >= 100 ? 0 : 2,
  })}`;
};

const formatPrice = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "N/A";
  }
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: value >= 1 ? 4 : 6,
  })}`;
};

const formatPnlPercent = (value: number): string => {
  const absValue = Math.abs(value);
  if (absValue < 1) {
    return value.toFixed(3);
  }
  if (absValue < 100) {
    return value.toFixed(2);
  }
  return Math.round(value).toString();
};

const buildMessage = (position: PositionData | ClosedPositionData): string => {
  const closed = isClosedPosition(position);
  const openPosition = getOpenPosition(position);
  const amountRaw = closed
    ? position.usdAmount
    : Number(position.usdAmount || position.amount);
  const leverageRaw = closed ? position.leverage : Number(position.leverage);
  const entryRaw = closed ? position.entryPrice : Number(position.entryPrice);
  const exitRaw = closed
    ? (position.exitPrice ?? position.entryPrice)
    : Number(
        openPosition?.markPrice ||
          openPosition?.exitPrice ||
          openPosition?.entryPrice ||
          0
      );
  const pnlRaw = closed
    ? (position.realizedPnl ?? 0)
    : Number(openPosition?.pnl ?? 0);
  // const pnlPercent = closed ? position.pnlPercentage ?? 0 : Number(openPosition?.pnlPercentage ?? 0);

  const lines: string[] = [
    `Position ${closed ? "Closed" : ""} 📊`,
    ``,
    `Type: ${position.positionType}`,
    `Amount: ${formatUsd(Number(amountRaw))}`,
    `Leverage: ${Number(leverageRaw)
      .toFixed(2)
      .replace(/\\.00$/, "")}x`,
    "",
    `Entry: ${formatPrice(Number(entryRaw))}`,
    `${closed ? "Exit" : "Mark"}: ${formatPrice(Number(exitRaw))}`,
    "",
    `${pnlRaw >= 0 ? "Profit" : "Loss"}: ${formatUsd(Math.abs(pnlRaw))}`,
  ];

  return lines.join("\n");
};

const getTemplateForPosition = (
  position: PositionData | ClosedPositionData
): CardTemplate => {
  const closed = isClosedPosition(position);
  const pnlValue = closed
    ? (position.realizedPnl ?? 0)
    : Number(getOpenPosition(position)?.pnl ?? 0);
  if (pnlValue > 0.0001) {
    return "profit";
  }
  if (pnlValue < -0.0001) {
    return "loss";
  }
  return "neutral";
};

const getBaseSymbol = (symbol: string): string => {
  const normalized = symbol.toUpperCase();
  return normalized
    .split("USDT")
    .join("")
    .split("USDM")
    .join("")
    .split("PERP")
    .join("");
};

export const PnlCardShare: React.FC<PnlCardShareProps> = ({
  position,
  userDisplayName,
  referralLink,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [supportsShare, setSupportsShare] = useState(false);

  useEffect(() => {
    if (typeof document !== "undefined" && !canvasRef.current) {
      canvasRef.current = document.createElement("canvas");
    }
    if (typeof navigator !== "undefined" && "canShare" in navigator) {
      setSupportsShare(true);
    }
  }, []);

  const templatePath = useMemo(
    () => CARD_TEMPLATE_PATHS[getTemplateForPosition(position)],
    [position]
  );
  const message = useMemo(() => buildMessage(position), [position]);
  const ticker = useMemo(
    () => getBaseSymbol(position.symbol),
    [position.symbol]
  );
  const sanitizedReferralLink = useMemo(
    () => referralLink?.trim() || null,
    [referralLink]
  );

  const drawParagraph = (
    ctx: CanvasRenderingContext2D,
    words: string[],
    params: {
      x: number;
      width: number;
      lineHeight: number;
      maxY: number;
      currentY: number;
    }
  ): { nextY: number; stop: boolean } => {
    let currentLine = "";
    let workingY = params.currentY;

    const pushLine = (line: string) => {
      ctx.fillText(line, params.x, workingY);
      workingY += params.lineHeight;
    };

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = ctx.measureText(testLine);
      if (metrics.width <= params.width) {
        currentLine = testLine;
      } else {
        if (currentLine) {
          pushLine(currentLine);
        }
        currentLine = word;
      }

      if (workingY > params.maxY) {
        return { nextY: workingY, stop: true };
      }
    }

    if (currentLine) {
      pushLine(currentLine);
    }

    return { nextY: workingY, stop: workingY > params.maxY };
  };

  const drawMessageBlock = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    width: number,
    height: number,
    lineHeight: number
  ) => {
    const paragraphs = text.split("\n");
    let currentY = y;
    const maxY = y + height;

    for (let index = 0; index < paragraphs.length; index += 1) {
      const words = paragraphs[index].split(" ");
      const { nextY, stop } = drawParagraph(ctx, words, {
        x,
        width,
        lineHeight,
        maxY,
        currentY,
      });
      currentY = nextY;

      if (stop) {
        return;
      }

      if (index < paragraphs.length - 1) {
        currentY += lineHeight * 0.5;
      }

      if (currentY > maxY) {
        return;
      }
    }
  };

  const generateCard = useCallback(async () => {
    if (!canvasRef.current) {
      return;
    }

    setIsGenerating(true);
    const image = new Image();
    image.src = templatePath;

    const imageLoadPromise = new Promise<HTMLImageElement>(
      (resolve, reject) => {
        image.onload = () => resolve(image);
        image.onerror = reject;
      }
    );

    try {
      const loadedImage = await imageLoadPromise;
      const canvas = canvasRef.current;
      const context = canvas?.getContext("2d");

      if (!canvas || !context) {
        throw new Error("Canvas context unavailable");
      }

      canvas.width = loadedImage.width || CARD_WIDTH;
      canvas.height = loadedImage.height || CARD_HEIGHT;

      context.drawImage(loadedImage, 0, 0, canvas.width, canvas.height);

      context.fillStyle = "#FFFFFF";
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.font = 'bold 60px "Inter", "Arial", sans-serif';
      context.fillText(ticker, 310, 255);

      context.textAlign = "left";
      context.textBaseline = "top";
      context.font = '28px "Inter", "Arial", sans-serif';
      drawMessageBlock(
        context,
        message,
        MESSAGE_AREA.x,
        MESSAGE_AREA.y,
        MESSAGE_AREA.width,
        MESSAGE_AREA.height,
        34
      );

      const closed = isClosedPosition(position);
      const openPosition = getOpenPosition(position);
      const pnlPercent = closed
        ? (position.pnlPercentage ?? 0)
        : Number(openPosition?.pnlPercentage ?? 0);
      const pnlText = `${formatPnlPercent(pnlPercent)}%`;
      const pnlValue = closed
        ? (position.realizedPnl ?? 0)
        : Number(openPosition?.pnl ?? 0);

      context.textAlign = "center";
      context.textBaseline = "middle";
      context.font = 'bold 140px "Inter", "Arial", sans-serif';
      context.fillStyle = pnlValue >= 0 ? "#00FFA7" : "#FF0000";
      context.fillText(pnlText, 330, 450);

      context.textAlign = "left";
      context.textBaseline = "middle";
      context.font = '30px "Inter", "Arial", sans-serif';
      context.fillStyle = "#94979C";
      context.fillText(userDisplayName, 1190, 120);

      if (sanitizedReferralLink) {
        try {
          const qrCanvas = document.createElement("canvas");
          await QRCode.toCanvas(qrCanvas, sanitizedReferralLink, {
            width: QR_CODE_SIZE,
            margin: 0,
            color: {
              dark: "#000000",
              light: "#FFFFFF",
            },
            errorCorrectionLevel: "L",
          });
          context.drawImage(
            qrCanvas,
            QR_CODE_POSITION.x,
            QR_CODE_POSITION.y,
            QR_CODE_SIZE,
            QR_CODE_SIZE
          );
        } catch (qrError) {
          console.error("Failed to render referral QR code", qrError);
        }
      }

      const dataUrl = canvas.toDataURL("image/png");
      setPreviewUrl(dataUrl);
    } catch (error) {
      console.error("Failed to generate PnL card", error);
      setPreviewUrl(null);
    } finally {
      setIsGenerating(false);
    }
  }, [
    message,
    position,
    sanitizedReferralLink,
    templatePath,
    ticker,
    userDisplayName,
  ]);

  useEffect(() => {
    if (canvasRef.current) {
      generateCard();
    }
  }, [generateCard]);

  const downloadCard = useCallback(() => {
    if (!previewUrl) {
      return;
    }
    const link = document.createElement("a");
    const baseSymbol = getBaseSymbol(position.symbol);
    link.href = previewUrl;
    link.download = `pnl_card_${baseSymbol}_${Date.now()}.png`;
    link.click();
  }, [position.symbol, previewUrl]);

  const shareCard = useCallback(async () => {
    if (!previewUrl || !supportsShare) {
      downloadCard();
      return;
    }
    try {
      const response = await fetch(previewUrl);
      const blob = await response.blob();
      const file = new File([blob], "pnl-card.png", { type: blob.type });
      const navigatorWithShare = navigator as Navigator & {
        canShare?: (data: ShareData) => boolean;
      };

      if (
        navigatorWithShare.canShare &&
        navigatorWithShare.canShare({ files: [file] })
      ) {
        await navigatorWithShare.share({
          files: [file],
          title: "PnL Card",
          text: sanitizedReferralLink
            ? `Sharing my ${getBaseSymbol(position.symbol)} trade results. Join me: ${sanitizedReferralLink}`
            : `Sharing my ${getBaseSymbol(position.symbol)} trade results.`,
        });
      } else {
        shareViaTelegram();
        downloadCard();
      }
    } catch (error) {
      console.error("Failed to share card", error);
      downloadCard();
    }
  }, [
    downloadCard,
    position.symbol,
    previewUrl,
    sanitizedReferralLink,
    supportsShare,
  ]);

  const shareViaTelegram = useCallback(() => {
    const url = sanitizedReferralLink ?? DEFAULT_BOT_LINK;
    const message = `Check out my ${getBaseSymbol(position.symbol)} trade on Trigger. ${
      sanitizedReferralLink ? "Join me:" : "Start trading:"
    } ${url}`;

    try {
      shareURL(url, message);
    } catch (error) {
      console.error("Failed to share via Telegram", error);
    }
  }, [position.symbol, sanitizedReferralLink]);

  return (
    <div className="space-y-3">
      <div className="w-full flex justify-center">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="PnL card preview"
            className="w-full max-w-sm rounded-xl border border-gray-500/30 shadow-lg"
          />
        ) : (
          <div className="w-full max-w-sm h-60 rounded-xl border border-dashed border-gray-500/40 flex items-center justify-center text-text-secondary text-sm">
            {isGenerating ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating card...
              </span>
            ) : (
              "Preview unavailable"
            )}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={shareCard}
        disabled={!previewUrl || isGenerating}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-accent text-white text-sm font-semibold hover:bg-accent/80 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <Share2 className="w-4 h-4" />
        Share
      </button>
      <p className="text-center text-xs text-text-secondary">
        {sanitizedReferralLink
          ? "QR code includes your personal referral link."
          : "Connect/refetch your referral link to embed it on the card."}
      </p>
    </div>
  );
};
